import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { ReactNode } from 'react';
import { MicrophoneService, type MicrophoneDevice, type MicrophoneServiceConfig } from '@/services/implementations/MicrophoneService';

// 低频状态：权限、设备、配置等
interface MicrophoneStateContextValue {
  // State
  permissionStatus: 'prompt' | 'granted' | 'denied' | 'unknown';
  hasPermission: boolean;
  availableDevices: MicrophoneDevice[];
  selectedDeviceId: string;
  isLoading: boolean;
  
  // Actions
  requestPermission: () => Promise<boolean>;
  selectDevice: (deviceId: string) => void;
  enumerateDevices: () => Promise<void>;
  
  // Service access
  microphoneService: MicrophoneService | null;
}

// 高频状态：音频电平等实时数据
interface MicrophoneAudioContextValue {
  audioLevel: number;
}

const MicrophoneStateContext = createContext<MicrophoneStateContextValue | null>(null);
const MicrophoneAudioContext = createContext<MicrophoneAudioContextValue | null>(null);

interface MicrophoneProviderProps {
  children: ReactNode;
}

export function MicrophoneProvider({ children }: MicrophoneProviderProps) {
  // Add unique ID for tracking
  const contextId = useRef(`MicCtx_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`);

  // 低频状态
  const [permissionStatus, setPermissionStatus] = useState<'prompt' | 'granted' | 'denied' | 'unknown'>('unknown');
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [availableDevices, setAvailableDevices] = useState<MicrophoneDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // 高频状态
  const [audioLevel, setAudioLevel] = useState<number>(0);

  // Use ref to store the service instance to ensure it's created only once
  const microphoneServiceRef = useRef<MicrophoneService | null>(null);

  // Create stable callback configuration that won't change between renders
  const createMicrophoneConfig = useCallback((): MicrophoneServiceConfig => {
    const config: MicrophoneServiceConfig = {
      onAudioData: (_audioData: Float32Array) => {
        // Handle real-time audio data if needed
        // This could be used for audio visualization or processing
      },
      onPermissionChange: (granted: boolean) => {
        console.log(`[🎤 MIC] 📋 [${contextId.current}] Permission changed to`, granted);
        setHasPermission(granted);
        setPermissionStatus(granted ? 'granted' : 'prompt');
      },
      onDevicesChange: (devices: MicrophoneDevice[]) => {
        console.log(`[🎤 MIC] 🎛️ [${contextId.current}] Devices changed`, devices);
        setAvailableDevices(devices);
        
        // Auto-select default device if none selected
        setSelectedDeviceId(currentDeviceId => {
          if (devices.length > 0 && !currentDeviceId) {
            const defaultDevice = devices.find(d => d.deviceId === 'default' || d.deviceId === '') || devices[0];
            microphoneServiceRef.current?.setSelectedDeviceId(defaultDevice.deviceId);
            return defaultDevice.deviceId;
          }
          return currentDeviceId;
        });
      },
      onAudioLevel: (level: number) => {
        setAudioLevel(level);
      }
    };

    return config;
  }, [contextId]);

  // Initialize microphone service as singleton with stable configuration
  const createMicrophoneService = useCallback(() => {
    if (!microphoneServiceRef.current) {
      const micConfig = createMicrophoneConfig();
      microphoneServiceRef.current = new MicrophoneService(micConfig);
    
    } else {
      // double check - 如果配置丢失，重新设置
      if (!microphoneServiceRef.current._config.onAudioLevel || 
        !microphoneServiceRef.current._config.onAudioData ||
        !microphoneServiceRef.current._config.onPermissionChange ||
        !microphoneServiceRef.current._config.onDevicesChange) {
        const micConfig = createMicrophoneConfig();
        microphoneServiceRef.current._config = micConfig;
      }
    }
    return microphoneServiceRef.current;
  }, [createMicrophoneConfig, contextId]);

  // 入口：App 启动就初始化麦克风服务！！
  const microphoneService = createMicrophoneService();

  // Request permission action
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!microphoneService) return false;
    
    try {
      setIsLoading(true);
      const granted = await microphoneService.requestPermission();
      setPermissionStatus(granted ? 'granted' : 'denied');
      setHasPermission(granted);
      return granted;
    } catch (error) {
      console.error('[🎤 MIC] Error requesting microphone permission:', error);
      setPermissionStatus('denied');
      setHasPermission(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [microphoneService]);

  // Select device action
  const selectDevice = useCallback((deviceId: string) => {
    if (!microphoneService) return;
    
    setSelectedDeviceId(deviceId);
    microphoneService.setSelectedDeviceId(deviceId);
  }, [microphoneService]);

  // Enumerate devices action
  const enumerateDevices = useCallback(async () => {
    if (!microphoneService) return;
    
    try {
      await microphoneService.enumerateDevices();
    } catch (error) {
      console.error('[🎤 MIC] Error enumerating devices:', error);
    }
  }, [microphoneService]);

  // Initialize service when component mounts
  useEffect(() => {
    if (!microphoneService) return;

    console.log(`[🎤 MIC] 🔧 [${contextId.current}] Setting up service callbacks`);

    // Get initial state
    setPermissionStatus(microphoneService.getPermissionStatus());
    setHasPermission(microphoneService.getIsPermissionOK());
    setAvailableDevices(microphoneService.getAvailableDevices());
    setSelectedDeviceId(microphoneService.getSelectedDeviceId());

    // Check permission status using modern API with fallback
    const checkInitialPermission = async () => {
      try {
        console.log(`[🎤 MIC] 🔍 [${contextId.current}] Checking initial permission status...`);
        const status = await microphoneService.checkPermissionStatus();
        console.log(`[🎤 MIC] 📋 [${contextId.current}] Initial permission status:`, status);

        // Enumerate devices to get current state
        await microphoneService.enumerateDevices();
      } catch (error) {
        console.error(`[🎤 MIC] ❌ [${contextId.current}] Error checking initial permission:`, error);
        // Fallback to basic enumeration
        microphoneService.enumerateDevices();
      }
    };

    checkInitialPermission();
  }, [microphoneService, contextId]);

  // TODO: Cleanup on unmount
  useEffect(() => {
    return () => {
      if (microphoneServiceRef.current) {
        console.log(`[🎤 MIC] 🗑️ [${contextId.current}] Cleaning up microphone service`);
        microphoneServiceRef.current.destroy().catch(console.error);
        microphoneServiceRef.current = null;
      }
      console.log(`[🎤 MIC] 💀 [${contextId.current}] MicrophoneProvider - Destructor`);
    };
  }, [contextId]);

  // 使用 useMemo 稳定低频状态的 context value
  const stateContextValue = useMemo((): MicrophoneStateContextValue => ({
    permissionStatus,
    hasPermission,
    availableDevices,
    selectedDeviceId,
    isLoading,
    requestPermission,
    selectDevice,
    enumerateDevices,
    microphoneService
  }), [
    permissionStatus,
    hasPermission,
    availableDevices,
    selectedDeviceId,
    isLoading,
    requestPermission,
    selectDevice,
    enumerateDevices,
    microphoneService
  ]);

  // 高频状态的 context value（audioLevel 变化频繁，但只影响需要它的组件）
  const audioContextValue = useMemo((): MicrophoneAudioContextValue => ({
    audioLevel
  }), [audioLevel]);

  return (
    <MicrophoneStateContext.Provider value={stateContextValue}>
      <MicrophoneAudioContext.Provider value={audioContextValue}>
        {children}
      </MicrophoneAudioContext.Provider>
    </MicrophoneStateContext.Provider>
  );
}

// Hook for accessing low-frequency microphone state
export const useMicrophoneState = (): MicrophoneStateContextValue => {
  const context = useContext(MicrophoneStateContext);
  if (!context) {
    throw new Error('useMicrophoneState must be used within a MicrophoneProvider');
  }
  return context;
};

// Hook for accessing high-frequency audio data
export const useMicrophoneAudio = (): MicrophoneAudioContextValue => {
  const context = useContext(MicrophoneAudioContext);
  if (!context) {
    throw new Error('useMicrophoneAudio must be used within a MicrophoneProvider');
  }
  return context;
}; 